import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import LoadingSpinner from '../../loading-spinner';
import { AnthropicKeySection } from '../../managed-agents/anthropic-key-section';
import { importManagedAgent } from '../../../../lib/managed-agents/api';
import type {
  ManagedAgentList,
  WorkspaceKeyStatus,
} from '../../../../lib/managed-agents/types';
import { DEFAULT_BUDGET_DOLLARS, MAX_BUDGET_DOLLARS } from '../constants';
import {
  ReadyStep,
  StepTitle,
  type WizardFooter,
  WizardFrame,
  type WizardStep,
} from '../wizard-frame';
import {
  CheckStep,
  ChooseAgentStep,
  type ImportSettings,
  SettingsStep,
} from './wizard-steps';

const STEPS: WizardStep[] = [
  { id: 'access', label: 'API key' },
  { id: 'choose', label: 'Agent' },
  { id: 'settings', label: 'Settings' },
  { id: 'check', label: 'Test', optional: true },
];

type StepId = 'access' | 'choose' | 'settings' | 'check' | 'done';

interface ImportWizardProps {
  workspaceId: string;
  isAdmin: boolean;
  keyStatus: WorkspaceKeyStatus;
  onKeyChange: (status: WorkspaceKeyStatus) => void;
  remote: ManagedAgentList | null;
  remoteError: string | null;
  onClose: () => void;
  /** Called once the agent is imported. */
  onSaved: () => void;
  onRunSimulation: (agentId: string) => void;
}

interface ImportedAgent {
  id: string;
  name: string;
  version: number;
}

/** Access → Choose → Settings → Check, for one Claude Console agent. */
export function ImportWizard({
  workspaceId,
  isAdmin,
  keyStatus,
  onKeyChange,
  remote,
  remoteError,
  onClose,
  onSaved,
  onRunSimulation,
}: ImportWizardProps) {
  const [step, setStep] = useState<StepId>(
    keyStatus.connected ? 'choose' : 'access',
  );
  const [agentId, setAgentId] = useState('');
  const [settings, setSettings] = useState<ImportSettings>({
    environmentId: '',
    budget: DEFAULT_BUDGET_DOLLARS,
    greeting: '',
    toolPolicy: 'deny',
  });
  const [imported, setImported] = useState<ImportedAgent | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Default to the first environment once the list arrives.
  useEffect(() => {
    const first = remote?.environments[0]?.id;
    if (first && !settings.environmentId) {
      setSettings((current) => ({ ...current, environmentId: first }));
    }
  }, [remote, settings.environmentId]);

  const importedVersions = useMemo(
    () =>
      new Map(
        remote?.imported.map((row) => [
          row.anthropic_agent_id,
          row.anthropic_agent_version,
        ]),
      ),
    [remote],
  );

  const budgetCents = Math.round(Number(settings.budget) * 100);
  const budgetError =
    Number.isFinite(budgetCents) &&
    budgetCents >= 1 &&
    budgetCents <= MAX_BUDGET_DOLLARS * 100
      ? null
      : `Enter an amount between $0.01 and $${MAX_BUDGET_DOLLARS}.`;

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const result = await importManagedAgent({
        workspaceId,
        anthropicAgentId: agentId,
        environmentId: settings.environmentId,
        toolPolicy: settings.toolPolicy,
        budgetCents,
        greeting: settings.greeting,
      });
      setImported({
        id: result.agentId,
        name: result.name,
        version: result.version,
      });
      onSaved();
      toast.success(`${result.name} imported`);
      setStep('check');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const footer = ((): WizardFooter => {
    switch (step) {
      case 'access':
        return {
          back: { label: 'Cancel', onClick: onClose },
          next: {
            label: 'Continue',
            disabled: !keyStatus.connected,
            onClick: () => setStep('choose'),
          },
        };
      case 'choose':
        return {
          back: { label: 'Back', onClick: () => setStep('access') },
          next: {
            label: 'Continue',
            disabled: !agentId,
            onClick: () => setStep('settings'),
          },
        };
      case 'settings':
        return {
          back: { label: 'Back', onClick: () => setStep('choose') },
          next: {
            label: isImporting ? 'Importing…' : 'Import agent',
            disabled:
              isImporting || !settings.environmentId || Boolean(budgetError),
            onClick: handleImport,
          },
        };
      case 'check':
        return {
          skip: () => setStep('done'),
          next: { label: 'Finish', onClick: () => setStep('done') },
        };
      case 'done':
        return {
          back: { label: 'Back to agents', onClick: onClose },
          next: {
            label: 'Run a simulation',
            onClick: () => imported && onRunSimulation(imported.id),
          },
        };
    }
  })();

  const needsList = step === 'choose' || step === 'settings';

  return (
    <WizardFrame
      title="Import a Claude Managed Agent"
      steps={STEPS}
      current={step}
      onClose={onClose}
      footer={footer}
    >
      {step === 'access' && (
        <>
          <StepTitle>Connect your Anthropic account</StepTitle>
          <AnthropicKeySection
            workspaceId={workspaceId}
            isAdmin={isAdmin}
            status={keyStatus}
            onChange={onKeyChange}
          />
        </>
      )}

      {needsList && remoteError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {remoteError}
        </p>
      )}
      {needsList && !remote && !remoteError && <LoadingSpinner />}

      {step === 'choose' && remote && (
        <ChooseAgentStep
          agents={remote.agents}
          importedVersions={importedVersions}
          selectedId={agentId}
          onSelect={setAgentId}
        />
      )}

      {step === 'settings' && remote && (
        <SettingsStep
          environments={remote.environments}
          settings={settings}
          onChange={setSettings}
          budgetError={budgetError}
        />
      )}

      {step === 'check' && imported && (
        <CheckStep
          workspaceId={workspaceId}
          anthropicAgentId={agentId}
          environmentId={settings.environmentId}
          agentId={imported.id}
        />
      )}

      {step === 'done' && imported && <ReadyStep name={imported.name} />}
    </WizardFrame>
  );
}
